import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, effect, ElementRef, HostListener, inject, OnDestroy, Signal, signal, ViewChild, ViewEncapsulation, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatToolbarModule } from '@angular/material/toolbar';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { addDays } from 'date-fns';
import moment from 'moment';
import { BehaviorSubject, firstValueFrom, Subject, Subscription } from 'rxjs';
import { first, take, takeUntil } from 'rxjs/operators';

// Services
import { AttachmentService } from 'src/app/services/api/attachments/attachment.service';
import { ModelService } from 'src/app/services/api/models/model.service';
import { PlanningService } from 'src/app/services/api/plannings/planning.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { AuthService } from 'src/app/services/core/auth.service';
import { PlanningStore } from 'src/app/services/stores/planning.store';

// Interfaces
import { lowerBound, upperBound } from 'src/app/classes/shared/dev.tools';
import { BuildingTypeNode, Fragment, LevelNode, ObjectTree, ObjectTreeFragmentData } from 'src/app/interfaces/api/fragment';
import { Metric, TaskMetric } from 'src/app/interfaces/api/metric';
import { Model, ModelProperty } from 'src/app/interfaces/api/model';
import { Planning } from 'src/app/interfaces/api/planning';
import { PlanningTask, PostTask } from 'src/app/interfaces/api/task';
import { Config } from 'src/app/interfaces/shared/config';
import { QueryParams } from 'src/app/interfaces/shared/query';

// Classes
import { PlanningTools } from 'src/app/classes/planning/planning-tools';
import { Pop } from 'src/app/classes/shared/pop';

// Components
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ModelDialogComponent } from 'src/app/components/project-pages/planning/dialogs/model/model-dialog.component';
import { PlanningPlayerComponent } from 'src/app/components/project-pages/planning/planning-player/planning-player.component';
import { PlanningViewComponent } from 'src/app/components/project-pages/planning/planning-view/planning-view.component';
import { TaskInfoComponent } from 'src/app/components/project-pages/planning/task-info/task-info.component';
import { PlanningDisplayModel } from 'src/app/interfaces/shared/planning-display';
import { WebSocketService } from 'src/app/services/core/web-socket.service';
import { ButtonComponent, SpinnerComponent } from 'ui-library';
import { ViewerComponent } from './viewer/viewer.component';

@Component({
  selector: 'app-planning',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatSlideToggleModule,
    TranslateModule,
    PlanningViewComponent,
    ViewerComponent,
    TaskInfoComponent,
    SpinnerComponent,
    PlanningPlayerComponent,
    ButtonComponent,
    MatProgressBarModule
  ],
  providers: [
    PlanningTools
  ],
  templateUrl: './planning.component.html',
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./planning.component.scss']
})

export class PlanningComponent implements AfterViewInit, OnDestroy {
  // Services
  private readonly attachmentService: AttachmentService = inject(AttachmentService);
  private readonly authService: AuthService = inject(AuthService);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly pop: Pop = inject(Pop);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly router: Router = inject(Router);
  private readonly planningService: PlanningService = inject(PlanningService);
  private readonly projectService: ProjectService = inject(ProjectService);
  private readonly modelService: ModelService = inject(ModelService);
  private readonly planningTools: PlanningTools = inject(PlanningTools);
  public readonly planningStore: PlanningStore = inject(PlanningStore);
  private readonly webSocketService: WebSocketService = inject(WebSocketService);

  // Components API
  @ViewChild("content", { static: false }) contentContainer!: ElementRef;
  @ViewChild("viewer", { static: false }) viewerContainer!: ViewerComponent;
  @ViewChild('board') board!: ElementRef;
  @ViewChild("resize", { static: false }) resize!: ElementRef;
  @ViewChild('planningFileInput') planningFileInput!: ElementRef<HTMLElement>;
  @ViewChild('planningView') planningView!: PlanningViewComponent;

  // Signals  
  currentTask: WritableSignal<PlanningTask | null> = this.planningStore.currentTask;
  loadingPlanning = signal<boolean>(false);
  selectedMetrics = signal<Metric[]>([]);
  model = signal<Model | null>(null);
  currentMetric = signal<TaskMetric | null>(null);
  selectedFragments = signal<Fragment[]>([]);
  selectedTasks = signal<PlanningTask[]>([]);
  planning = signal<Planning>({} as Planning);
  show3D: WritableSignal<boolean> = signal<boolean>(true);
  showGantt: WritableSignal<boolean> = signal<boolean>(true);

  readonly isLoadingModel: WritableSignal<boolean> = this.planningStore.isLoadingModel;
  readonly isLoadingTasks: WritableSignal<boolean> = this.planningStore.isLoadingTasks;
  readonly isLoadingMetrics: WritableSignal<boolean> = this.planningStore.isLoadingMetrics;
  readonly isLoadingFragments: WritableSignal<boolean> = this.planningStore.isLoadingFragments;

  readonly showUnlinked: Signal<boolean> = this.planningStore.showUnlinked;

  // Variables
  //route variables
  planningId = this.route.snapshot.queryParamMap.get('id') ?? '';
  currentDate = this.route.snapshot.queryParamMap.get('startDate')

  user: any;
  project: any;
  destroy$ = new Subject<void>();

  lines: any[] = [];
  years: any[] = [];
  months: any[] = [];
  days: any[] = [];
  weeks: any[] = [];
  hours: any[] = [];
  minutes: any[] = [];
  startDate = moment().subtract(1, "year").toISOString();
  endDate = moment().toISOString();
  startOfDay = moment().startOf('day');
  startOfWeek = moment().startOf('week');
  timelineMoment: any = moment();
  hourSize = 0.1;//px
  daySize = this.hourSize * 24;
  weekSize = this.daySize * 7;
  dayCount = 0;
  weekLeft = 0;
  scale: number = 1;
  coef: number = 1;
  darkBarTop: number = 0;
  dateBarLeft: number = 0;
  darkBarHeight: number = 0;
  darkBarBottom: number = 0;
  minuteWidth: number = 8.3334;
  hoverInfo: boolean = false;
  keyPressed = "";
  darkBarVisible = true;
  initialScales: string[] = ["threeYears", "year", 'semester', 'quarter', 'month', 'halfMonth', 'week', 'halfWeek', 'day']
  scales: string[] = [...this.initialScales];
  scaleIndex: number = 0;
  hourSizes: Record<string, number> = {};
  calendarWidth: number = 500;
  hourWidth: number = 50;
  dayCounts: Record<string, number> = { threeYears: 365 * 3, year: 365, semester: 30 * 3 + 3 * 31, quarter: 30 * 3, month: 31, halfMonth: 15, week: 7, halfWeek: 3, day: 1 };

  ganttWidth = signal<number>(500);
  contentWidth: number = 100;
  currentScale: string = "month";
  scrollLeft: number = 0;
  scrollTop: number = 0;
  graphHeight: number = 100;
  lineHeight: number = 30;
  treeWidth: number = 260;
  barDeltaX: number = 0;
  startBarDeltaX: number = 0;
  startBarPosition: number = 0;
  dateBarPosition: number = 230;
  isSidebarOpen: boolean = false;

  timelineBound: any = { x: 205, y: 0 };
  loadedDates = [];
  mouse: any = { x: 0, y: 0, isDown: false, dX: 0, dY: 0 };
  dragged: string = '';
  status = [
    {
      name: 'done',
      color: Config.colors.success
    }, {
      name: 'progress',
      color: Config.colors.primary
    },
    {
      name: 'todo',
      color: Config.colors.danger
    }
  ];

  craneCycles = [{
    name: "concreting",
    color: Config.colors.danger
  },
  {
    name: "formwork",
    color: Config.colors.warning
  },
  {
    name: "prefabricated",
    color: Config.colors.primary
  },
  {
    name: "reinforcement",
    color: Config.colors.success
  },
  {
    name: "other",
    color: Config.colors.info
  }];

  units: any[] = [];
  updatingPlanning: boolean = false;
  updateProgress: number = 0;
  updateTotal: number = 0;
  helpLink = 'https://cad42.atlassian.net/wiki/x/AYBhKQ';

  //player 
  playerTimeText: any;
  playerValue: BehaviorSubject<any>;

  barPosition: number = 100;
  loadingFragment: boolean = false;
  // fragments: any;
  fragmentById: Record<string, Fragment> = {};
  parsingFragment: string = '';
  refreshProgress: number = 0;
  refreshTotal: number = 0;
  planningExt: any;
  autolinkAvailable: any;
  isEditing = false;
  tasksOrder: number[] = [];

  session: Record<string, string> = {};
  attachments: any[] = [];
  limit: number[] = [];
  offset: number[] = [];
  scrollLoading: boolean = false;
  metricsByTask: Record<string, any[]> = {}; // { [taskId]: metrics[] }
  scrollThreshold = 200;
  previousTasksState: any;
  draggedTaskIndex: number | null = null;
  showDropLine: boolean = false;
  hoverIndex: number | null = null;
  isNesting: boolean = false;
  isDragEnabled: boolean = false;
  firstZoom: boolean = false;
  state = {
    showHours: false,
    showDays: false,
    showWeeks: false,
    showMonths: true,
    showYears: true
  };
  cachedDays: any;
  cachedMonths: any;
  cachedYears: any;
  cachedWeeks: { start: string; end: string; data: any[]; } | null = null;
  cachedHours: { start: string; end: string; data: any[]; } | null = null;
  cachedMinutes: any;

  dataLoaded = {
    planning: false,
    fragment: false,
    metric: false
  }
  viewer: any;
  planningUrl: string = '';
  modelProperties: ModelProperty[] = [];
  buildingTypes: any;
  private planningUpdateSub?: Subscription;
  autoLinkSub: Subscription | null = null;
  autoLinking: boolean = false;
  autoLinkTotal: number = 0;
  autoLinkProgress: number = 0;
  autoLinkDone: boolean = false;

  // Listeners
  @HostListener('mousemove', ['$event']) // for window scroll events
  onMouseMove(event: MouseEvent) {
    if (this.dragged && this.dragged.includes("mat-input-")) return;

    // if (document.selection) {
    //   document.selection.empty()
    // } else {
    window.getSelection()?.removeAllRanges()
    // }
    this.mouse.x = event.x;
    this.mouse.y = event.y;

    if (event.buttons == 0) this.mouse.isDown = false;
    if (this.mouse.isDown) {
      if (this.isDragEnabled)
        return;

      if (this.dragged == 'resize-handler') {
        this.ganttWidth.set(Math.max(Math.floor(this.ganttWidth() + event.movementX), 0));
      }
    }
  }

  @HostListener('mouseup', ['$event']) // for window scroll events
  onMouseUp(event: MouseEvent) {
    this.mouse.isDown = false;
    if (this.dragged == 'resize-handler') {
      this.viewer.resize()
    }
  }

  @HostListener('mousedown', ['$event']) // for window scroll events
  onMouseDown(event: MouseEvent) {
    this.mouse.isDown = true;
    this.mouse.dX = event.x;
    this.mouse.dY = event.y;
    this.dragged = (event.target as HTMLElement)?.id || '';
  }

  constructor() {
    this.user = this.authService.currentUserValue;
    this.planningId = this.route.snapshot.queryParamMap.get('id') || '';

    this.projectService.currentProject.pipe(take(1)).subscribe(project => {
      this.project = project
    })

    // this.loadMetrics(this.startDate, this.endDate);
    this.playerValue = new BehaviorSubject(0);

    effect(() => {
      const planning = this.planningStore.planning();
      this.planning.set(planning)

      if (planning && planning.model) {
        const modelId = typeof planning.model === 'string' ? planning.model : planning.model.id;
        this.modelService.getModel(modelId).pipe(take(1)).subscribe(model => {
          this.model.set(model);
        })
      }
    })

    effect(() => {
      const currentDate = this.planningStore.currentDate();

      this.router.navigate([], {
        queryParams: { id: this.planningId, startDate: currentDate },
        queryParamsHandling: 'merge',
      });
    })

    // this.router.navigate(['/project/planning'], { queryParams: { id: planningId } })
    this.loadPlanning();
  }

  /////////////////////////////
  // Angular lifecycle hooks //
  /////////////////////////////
  ngAfterViewInit(): void {
    if (this.currentDate)
      this.planningStore.setCurrentDate(this.currentDate);

    this.contentWidth = this.contentContainer.nativeElement.clientWidth;
    this.ganttWidth.set(this.show3D() ? this.contentWidth / 2 : this.contentWidth - 230);
  }

  ngOnDestroy() {
    this.planningUpdateSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Loading cascade
   */
  loadPlanning() {
    this.planningService.loadPlanning(this.planningId);
  }

  get loadingProgress(): number {
    const flags = [
      this.planningStore.isLoadingModel(),
      this.planningStore.isLoadingTasks(),
      this.planningStore.isLoadingFragments(),
      this.planningStore.isLoadingMetrics()
    ];

    const total = flags.length;
    const loaded = flags.filter(v => !v).length;
    return Math.round((loaded / total) * 100);
  }

  get currentLoadingLabel(): string | null {
    if (this.isLoadingTasks()) return 'planning.loading.loadingTasks';
    if (this.isLoadingFragments()) return 'planning.loading.loadingFragments';
    if (this.isLoadingMetrics()) return 'planning.loading.loadingMetrics';
    if (this.isLoadingModel()) return 'planning.loading.loadingModel';
    return null;
  }

  /**
   * Parsing
   */
  async updateFragments() {
    try {
      this.viewerContainer.updateFragments().subscribe(result => {

      });
    } catch (e) {
      console.error(e);
    }
  }

  async refreshFragDb() {
    await this.showRefreshFragDbStatus();
  }

  /**
   * Events
   */
  onLoaded3D(viewer: any) {
    this.viewer = viewer;
  }

  getMetricAttachments(metric: Metric) {
    if (metric.attachments)
      return metric.attachments;

    metric.attachments = [];

    this.attachmentService.getAttachments({
      startDate: metric.payload?.startDate || '',
      endDate: metric.payload?.endDate || '',
      limit: 1,
      offset: 0
    }).subscribe((attachments: any) => {
      attachments.sort((a: any, b: any) => moment(a.createDate).isBefore(b.createDate) ? 1 : -1);
      metric.attachments = attachments;
    });

    return metric.attachments;
  }

  selectMetrics(metrics: Metric[]) {
    this.selectedMetrics.set(metrics)
  }

  selectFragments(fragDbIds: string[]) {
    // TODO: implement with fragments instead of fragDbIds (and still it lacks any substance)
    // this..selectedFragments$.next(fragDbIds)
  }

  updateSideInfo() {
    if (!this.currentTask) return;
    // this.updateTask(this.currentTask)
  }

  setZoom(val: number) {
    if (this.planningView) {
      this.planningView.setZoom(val);
    }
  }

  deleteTask() {
    const taskId = this.currentTask()?.id;

    if (!taskId) return;

    this.planningService.removeTask(taskId).pipe(first()).subscribe({
      next: () => {
        this.planningStore.removeTask(taskId);
      },
      error: error => {
        console.error('Erreur chargement tasks:', error);
      }
    });
  }

  updateTasksOrder(): void {
    this.planningService.updatePlanning({ id: this.planningId, tasksOrder: this.tasksOrder, lastUpdate: Date.now() }).pipe(take(1)).subscribe(res => {
      console.log("res planning->", res);
    });
  }

  setMapping() {
    const dialogRef = this.dialog.open(ModelDialogComponent, {
      width: '70%',
      hasBackdrop: false,
      data: {
        viewer: this.viewer,
        model: this.model(),
        modelProperties: this.modelProperties,
        buildingTypes: this.buildingTypes
      }
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (data.properties && data.properties.length) {
        this.modelProperties = data.properties;
        this.buildingTypes = data.buildingTypes;
      }
    })
  }

  buildObjectTree(): ObjectTree {
    let objectTree: ObjectTree = {};
    let model = this.model();

    if (!model)
      return objectTree;

    let buildingTypes = Object.keys(model.buildingTypes!).reduce((p: string[], c: string) => {
      return p.concat(model.buildingTypes![c] || []);
    }, [] as string[])

    this.planningStore.fragments().forEach((frag: Fragment) => {
      if (!frag.mapping) return

      let taskName = frag.mapping.planning?.taskName;
      let building = frag.mapping.location?.building;
      let level = taskName || frag.mapping.location?.level;
      let buildingType = frag.mapping.element?.buildingType;

      if (!buildingType || !buildingTypes.includes(buildingType)) return;

      // let formattedBuildingType = this.planningTools.getFormattedBuildingType(this.model, buildingType);
      let volume = frag.mapping.dimension?.volume || frag.volume;
      let z = frag.z;
      let height = frag.height;

      const setLevel = (parent: LevelNode | BuildingTypeNode) => {
        if (level) {
          if (!parent[level])
            parent[level] = {};

          setBuildingType(parent[level] as BuildingTypeNode)
        } else {
          setBuildingType(parent as BuildingTypeNode)
        }
      }

      const setBuildingType = (parent: BuildingTypeNode) => {
        if (buildingType) {
          if (!parent[buildingType])
            parent[buildingType] = [];

          const fragmentData: ObjectTreeFragmentData = {
            name: frag.name,
            id: frag.id,
            dbId: frag.dbId,
            duration: this.planningTools.getBuildingDuration(model.buildingTypes ?? {}, buildingType, volume as number),
            volume,
            height,
            z
          };

          parent[buildingType].push(fragmentData)
        } else {
          parent["undefined"] = []

          const fragmentData: ObjectTreeFragmentData = {
            name: frag.name,
            id: frag.id,
            dbId: frag.dbId,
            duration: 1,
            volume,
            height,
            z
          };

          parent["undefined"].push(fragmentData)
        }
      }

      if (building) {
        if (!objectTree[building])
          objectTree[building] = {};

        setLevel(objectTree[building] as LevelNode)
      } else {
        setLevel(objectTree as unknown as LevelNode)
      }
    })

    return objectTree;
  }

  setTaskOrder(tasks: PlanningTask[]): PlanningTask[] {
    let _tasks: PlanningTask[] = [];
    tasks.sort((a, b) => a.z && b.z && a.z > b.z ? 1 : -1);

    tasks.forEach((t, i: number) => {
      // tasks.push(_tasks[0]);
      _tasks.push(t)

      if (t.children && t.children.length) {
        _tasks = _tasks.concat(this.setTaskOrder(t.children));
      }
    })

    return _tasks;
  }

  buildTaskFromTree(objectTree: ObjectTree) {
    let tasks: PlanningTask[] = [];
    let taskTree: PlanningTask[] = [];
    let firstDate = this.project.startDate || moment().startOf("day").toISOString();
    let num = 0;

    const getFragData = (array: ObjectTreeFragmentData[], parentNum: number, indent: number) => {
      let data = {
        fragments: [] as Fragment[],
        fragmentIds: [] as string[],
        fragmentDbIds: [] as number[],
        volume: 0,
        duration: 0,
        cost: 0,
        height: 0,
        z: Infinity
      };

      array.forEach((elem: ObjectTreeFragmentData, i: number) => {
        data.fragmentIds.push(elem.id);

        if (elem.dbId !== undefined) {
          data.fragmentDbIds.push(elem.dbId);
        }

        const fragment = this.fragmentById[elem.id];
        if (fragment) {
          data.fragments.push(fragment);
        }

        if (elem.volume)
          data.volume += elem.volume;

        if (elem.duration)
          data.duration += elem.duration;

        if (elem.cost)
          data.cost += elem.cost;

        if (elem.z && elem.height)
          data.height = Math.max(data.height, data.z == Infinity ? elem.z - data.z + elem.height : elem.height)

        if (elem.z)
          data.z = Math.min(data.z, elem.z)
      })

      return data;
    }

    const parseObject = (obj: any, parentNum: number, indent: number) => {
      let _tasks: PlanningTask[] = [];

      Object.keys(obj).forEach((k, i) => {
        num++;
        let taskRoot: PlanningTask = { ...this.planningTools.defaultTask } as unknown as PlanningTask
        taskRoot.text = k;
        taskRoot.number = num;
        taskRoot.indent = indent;
        taskRoot.startDate = firstDate;
        taskRoot.parent = parentNum;
        taskRoot.z = Infinity;
        tasks.push(taskRoot);

        if (Array.isArray(obj[k])) {
          let { fragments, fragmentIds, fragmentDbIds, volume, cost, z, height, duration } = getFragData(obj[k], num, indent + 1)
          // taskRoot.fragments = fragments;
          // taskRoot.fragmentIds = fragmentIds;
          taskRoot.fragmentDbIds = fragmentDbIds;
          taskRoot.volume = volume;
          taskRoot.cost = cost;
          taskRoot.duration = Math.max(1, duration);

          if (z && z !== Infinity)
            taskRoot.z = z;

          if (height)
            taskRoot.height = height;
        } else {
          taskRoot.children = parseObject(obj[k], num, indent + 1);

          taskRoot.children.forEach(c => {
            if (c.z && taskRoot.z && c.z < taskRoot.z) {
              taskRoot.z = c.z;
            }

            if (c.height && taskRoot.height && c.height > taskRoot.height) {
              taskRoot.height = c.height;
            }

            if (!taskRoot.duration)
              taskRoot.duration = 0;

            taskRoot.duration += c.duration || 0;
          })
        }

        taskRoot.endDate = moment(firstDate).add(taskRoot.duration, "day").toISOString();
        _tasks.push(taskRoot)
      })

      // tasks = tasks.concat(_tasks);
      return _tasks;
    }

    const dispatchInTime = (tasks: PlanningTask[]) => {
      let handled: number[] = [];

      const handleChildren = (children: PlanningTask[], _firstDate: string) => {
        let startDate = _firstDate;
        let endDate = _firstDate;
        let duration = 1;

        children.forEach((c: PlanningTask, i: number) => {
          handled.push(c.number)
          c.startDate = startDate;

          if (!c.duration)
            c.duration = 0;

          if (c.children && c.children.length) {
            let { duration } = handleChildren(c.children, startDate)
            c.endDate = moment(startDate).add(duration, "day").toISOString();
          } else {
            c.endDate = moment(startDate).add(c.duration, "day").toISOString();
          }

          startDate = moment(startDate).add(c.duration / 2, "day").toISOString();

          if (moment(endDate).isBefore(c.endDate)) {
            endDate = c.endDate;
          }
        })

        duration = moment(endDate).diff(_firstDate, "day");
        return { duration };
      }

      tasks.forEach((t: PlanningTask) => {
        // tasks.push(_tasks[0]);
        if (t.children && t.children.length && !handled.includes(t.number)) {
          t.startDate = firstDate;
          let { duration } = handleChildren(t.children, firstDate);
          t.endDate = moment(firstDate).add(duration, "day").toISOString();
          firstDate = moment(firstDate).add(duration / 2, "day").toISOString();
          handled.push(t.number)
        }
      })
    }

    taskTree = parseObject(objectTree, num, 1);
    tasks = this.setTaskOrder(taskTree);
    dispatchInTime(tasks);
    // this.planning.tasksOrder = tasks.map(t => t.number);
    // this.planning.tasks = tasks;
    this.planningStore.updateTasks(tasks)
    // this.planningData$.next({ tasks });
    this.saveTasks([...tasks])
  }

  saveTasks(tasks: PlanningTask[]) {
    let taskToSend: PostTask[] = tasks.map(t => {
      let task = { ...t } as any;
      task.planning = this.planningId;
      task.children = [];
      delete task.parentTask;
      return task;
    })

    this.planningService.addTasks(taskToSend).pipe(first()).subscribe(res => {
      console.log("tasks", "saved")
    })
  }

  /**
    * Click events functions
    */

  /**
   * Import /export
   */
  planningImport() {
    let el: HTMLElement = this.planningFileInput.nativeElement;
    el.click();
  }

  refreshTasks() {
    let objectTree = this.buildObjectTree();
    this.buildTaskFromTree(objectTree);
  }

  resetFragments() {
    this.planningService.resetFragments(this.planningId).subscribe(() => {
      console.log("planning successfully reset")
    })
  }

  async runUpdate(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      let model = this.model();

      if (!model) return resolve();

      let query: any = { planning: this.planningId, model: model.id };
      let planning = this.planningStore.planning();

      if (planning)
        query.unit = planning.unit;

      this.updatingPlanning = true;
      this.planningUpdateSub?.unsubscribe();

      this.planningUpdateSub = this.webSocketService.getPlanningUpdateProgress()
        .subscribe(async ({ progress, total }) => {
          this.updateProgress = progress;
          this.updateTotal = total;

          if (progress >= total) {
            this.planningUpdateSub?.unsubscribe();
            this.updatingPlanning = false;
            await this.planningService.loadPlanning(this.planningId);
            resolve();
          }
        });

      this.planningService.runUpdate(query).pipe(take(1)).subscribe({
        error: () => {
          this.updatingPlanning = false;
          this.planningUpdateSub?.unsubscribe();
          reject();
        }
      });
    });
  }

  onPlayerChanged(event: any) {
    if (event.playerState == "forward-hold" || event.playerState == "reward-hold") {
      this.planningStore.setCurrentDate(event.startDate.toISOString());
    } else if (event.playerState == "forward") {
      let tasks = this.planningStore.tasks();
      const taskIndex = upperBound(tasks, this.planningStore.currentDate() as string, "startDate");

      if (taskIndex >= 0 && tasks[taskIndex + 1]?.startDate) {
        this.planningStore.setCurrentDate(tasks[taskIndex + 1]?.startDate as string)
      }
    } else if (event.playerState == "reward") {
      let tasks = this.planningStore.tasks();
      const taskIndex = lowerBound(tasks, this.planningStore.currentDate() as string, "startDate");

      if (taskIndex >= 0 && tasks[taskIndex - 1]?.startDate)
        this.planningStore.setCurrentDate(tasks[taskIndex - 1]?.startDate as string)
    } else if (event.playerState == "play") {
      this.planningStore.setCurrentDate(addDays(new Date(this.planningStore.currentDate() as string), 1).toISOString())
    }
  }

  onTaskChanged(task?: any) {
    if (!task) {
      this.deleteTask();
    }
  }

  onToggleDisplayModel(key: keyof PlanningDisplayModel, value: MatSlideToggleChange) {
    this.planningStore.setDisplayModel({ [key]: value.checked });
  }

  onToggleDisplayMetric(key: string, value: MatSlideToggleChange) {
    this.planningStore.setDisplayMetric({ [key]: value.checked });
  }

  updateShowUnlinked(val: boolean) {
    const query: any = {
      id: this.planningId,
      showUnlinked: val,
      lastUpdate: Date.now()
    };

    this.planningService.updatePlanning(query).pipe(take(1)).subscribe((updatedPlanning) => {
      this.planningStore.updatePlanning({
        ...this.planningStore.planning(),
        showUnlinked: val
      });
    });
  }

  async linkFragments(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.pop.confirm(this.translate.instant('planning.confirm_autolink'), async (confirm: any) => {
        const model = this.model();

        if (!confirm.ok || !model) return resolve();

        this.autoLinkDone = false;
        this.autoLinkProgress = 0;
        this.autoLinkTotal = 0;

        this.autoLinking = true;
        this.autoLinkSub?.unsubscribe();

        this.autoLinkSub = this.webSocketService.getAutoLinkProgress()
          .subscribe(async ({ progress, total }) => {
            this.autoLinkProgress = progress;
            this.autoLinkTotal = total;

            if (progress >= total) {
              this.autoLinkSub?.unsubscribe();
              this.autoLinking = false;
              this.autoLinkDone = true;

              setTimeout(() => this.autoLinkDone = false, 4000);

              await this.planningService.loadPlanning(this.planningId);
              resolve();
            }
          });

        this.planningService.autoLinkTaskFragments({
          planning: this.planningId,
          model: model.id
        }).pipe(take(1)).subscribe({
          error: (err) => {
            this.autoLinking = false;
            this.autoLinkSub?.unsubscribe();
            reject(err);
          }
        });
      });
    });
  }

  async uploadPlanningXML(fileupload: any) {
    const input = fileupload.target;
    this.dataLoaded.planning = false;

    for (let index = 0; index < input.files.length; index++) {
      const file = input.files[index];
      const reader = new FileReader();

      reader.onload = async () => {
        const object: QueryParams = {
          file: reader.result,
          planningId: this.planningId
        };

        try {
          console.log("1- IMPORT DU PLANNING --->");
          await firstValueFrom(this.planningService.addPlanningImport(object).pipe(takeUntil(this.destroy$)));

          console.log("2- RECHARGEMENT DU PLANNING");
          await this.planningService.loadPlanning(this.planningId);

          console.log("3- RAFRAICHISSEMENT DE LA VUE 3D");
          await this.showRefreshFragDbStatus();

          if (this.model()?.mapping?.planning?.taskId) {
            console.log("4- VERIFICATION DU MAPPING MODELE ET LIEN DES FRAGMENTS");
            await this.linkFragments();

            console.log("5- RECALCUL DES METRICS");
            await this.runUpdate();
          } else {
            console.log("4- METTRE LA VUE SANS GROUPES LES TASKS");
            this.updateShowUnlinked(true);
          }

          console.log("FINISHED IMPORT PROCESS");
        } catch (err) {
          console.error(err);
          this.loadingPlanning.set(false);
        }
      };

      reader.readAsText(file);
    }
  }

  // Missing output event handlers
  onTaskSelected(task: PlanningTask) {
    // Handle task selection from viewer
    this.onCurrentTaskChanged(task);
  }

  onClose() {
    // Handle close event from task-info
    this.currentTask.set(null);
  }

  closeSideBar() {
    this.currentTask.set(null);
    this.isSidebarOpen = false;
  }

  onShowGantt() {
    this.showGantt.set(!this.showGantt());
    this.ensureOneActive('gantt');
    this.updateGanttWidth();
  }

  onShow3D() {
    this.show3D.set(!this.show3D());
    this.ensureOneActive('3d');

    if (this.show3D()) {
      this.viewer.resize();
    }

    this.updateGanttWidth();
  }

  private ensureOneActive(state: 'gantt' | '3d') {
    if (!this.showGantt() && !this.show3D()) {
      if (state === 'gantt') {
        this.show3D.set(true);
      } else if (state === '3d') {
        this.showGantt.set(true);
      }
    }
  }

  private updateGanttWidth() {
    this.contentWidth = this.contentContainer.nativeElement.clientWidth;

    const treeElement = document.querySelector('#planning-gantt .tree');
    if (treeElement) {
      this.treeWidth = (treeElement as HTMLElement).clientWidth;
    }

    if (!this.showGantt()) {
      this.ganttWidth.set(this.treeWidth);
    } else if (this.show3D() && this.showGantt()) {
      this.ganttWidth.set(this.contentWidth / 2);
    } else {
      this.ganttWidth.set(this.contentWidth);
    }

    setTimeout(() => {
      this.viewer.resize();
    }, 0);
  }

  onCurrentTaskChanged(task: PlanningTask) {
    this.currentTask.set(task);
  }

  async showRefreshFragDbStatus(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.viewerContainer.refreshFragDb().subscribe({
        next: ({ status, progress, total }) => {
          this.parsingFragment = status;
          this.refreshProgress = progress;
          this.refreshTotal = total;

          if (status === "frag_saved") {
            setTimeout(() => this.parsingFragment = "", 4000);
            resolve();
          }
        },
        error: (error) => {
          this.parsingFragment = "frag_error";
          setTimeout(() => this.parsingFragment = "", 4000);
          reject(error);
        }
      });
    });
  }
}
